// src/pages/onboarding/OnboardingCreateBotPage.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "./OnboardingLayout";
import { createBot, CreateBotPayload, Bot } from "../../api/bots";

const OnboardingCreateBotPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState<{
    name: string;
    slug: string;
    description: string;
  }>({
    name: "",
    slug: "",
    description: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const payload: CreateBotPayload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || undefined,
        channelWeb: true,
        useDomainCrawler: true,
        usePdfCrawler: true
      };

      const bot: Bot = await createBot(payload);

      // Go to onboarding channels step
      navigate(`/onboarding/bots/${encodeURIComponent(bot.id)}/channels`);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message ||
          t(
            "botCreate.errors.createFailed",
            "Unable to create the bot. Please try again."
          )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingLayout
      currentStep="bot"
      title={t(
        "botCreate.onboarding.title",
        "Create your first Coslo bot"
      )}
      subtitle={t(
        "botCreate.onboarding.subtitle",
        "Give your assistant a name and a short description. You can change this later."
      )}
    >
      <form className="form" onSubmit={handleSubmit}>
        {error && <div className="form-error onboarding-form-error-txt">{error}</div>}

        <p className="muted" style={{ marginBottom: "1rem" }}>
          {t(
            "botCreate.onboarding.intro",
            "This information is used in the dashboard and in your internal tools, not in the public widget."
          )}
        </p>

        <label className="form-field">
          <span>{t("botCreate.fields.name", "Bot name")}</span>
          <input
            type="text"
            value={form.name}
            onChange={handleChange("name")}
            required
            placeholder={t(
              "botCreate.onboarding.namePlaceholder",
              "e.g. Acme Support Assistant"
            )}
          />
        </label>

        <label className="form-field">
          <span>{t("botCreate.fields.slug", "Bot slug")}</span>
          <input
            type="text"
            value={form.slug}
            onChange={handleChange("slug")}
            required
            placeholder={t(
              "botCreate.onboarding.slugPlaceholder",
              "e.g. acme-support"
            )}
          />
          <small>
            {t(
              "botCreate.fields.slugHelp",
              "Used in URLs and widget embedding. Lowercase, letters and dashes."
            )}
          </small>
        </label>

        <label className="form-field">
          <span>{t("botCreate.fields.description", "Short description")}</span>
          <textarea
            value={form.description}
            onChange={handleChange("description")}
            rows={2}
            placeholder={t(
              "botCreate.onboarding.descriptionPlaceholder",
              "Optional. A short sentence about what this bot does."
            )}
          />
        </label>

        <div className="form-actions">
          <button
            className="btn-primary"
            type="submit"
            disabled={isSubmitting || !form.name.trim() || !form.slug.trim()}
          >
            {isSubmitting
              ? t("botCreate.actions.creating", "Creating…")
              : t("botCreate.actions.create", "Create bot and continue")}
          </button>
        </div>
      </form>
    </OnboardingLayout>
  );
};

export default OnboardingCreateBotPage;
